import { PROVIDER_CONFIGS, type ProviderFile } from "./provider-config";

export enum SkillCollectionPresetValue {
  ClaudeCode = "claude_code",
  OpenAI = "openai",
  OpenClaw = "openclaw",
}

export type SkillCollectionPreset =
  | SkillCollectionPresetValue.ClaudeCode
  | SkillCollectionPresetValue.OpenAI
  | SkillCollectionPresetValue.OpenClaw;
export const VENDOR_AGNOSTIC_PROVIDER = "vendor_agnostic";
export type SkillCollectionProviderSelection =
  | SkillCollectionPreset
  | typeof VENDOR_AGNOSTIC_PROVIDER;

export type PresetFile = ProviderFile;

export const AVAILABLE_SKILL_COLLECTION_PRESETS: SkillCollectionPreset[] = [
  SkillCollectionPresetValue.ClaudeCode,
  SkillCollectionPresetValue.OpenAI,
  SkillCollectionPresetValue.OpenClaw,
];
export const AVAILABLE_PROVIDER_SELECTIONS: SkillCollectionProviderSelection[] = [
  VENDOR_AGNOSTIC_PROVIDER,
  ...AVAILABLE_SKILL_COLLECTION_PRESETS,
];

const deriveFromConfigs = <T>(
  fn: (config: (typeof PROVIDER_CONFIGS)[SkillCollectionPreset]) => T,
) =>
  Object.fromEntries(
    AVAILABLE_SKILL_COLLECTION_PRESETS.map(key => [key, fn(PROVIDER_CONFIGS[key])]),
  ) as Record<SkillCollectionPreset, T>;

export const PRESET_LABELS: Record<SkillCollectionPreset, string> = deriveFromConfigs(
  config => config.displayName,
);
export const PROVIDER_SELECTION_LABELS: Record<SkillCollectionProviderSelection, string> = {
  [VENDOR_AGNOSTIC_PROVIDER]: "Universal",
  ...PRESET_LABELS,
};

export const PRESET_LOGO_PROVIDERS: Record<SkillCollectionPreset, string> = deriveFromConfigs(
  config => config.logoProvider,
);

export const PRESET_PROTECTED_PATHS: Record<string, string[]> = deriveFromConfigs(
  config => config.protectedPaths,
);

export const getPresetFiles = (preset: SkillCollectionPreset): PresetFile[] => [
  ...PROVIDER_CONFIGS[preset].presetFiles,
];

export const isSkillCollectionPreset = (
  value: string | null | undefined,
): value is SkillCollectionPreset =>
  value !== null && value !== undefined && value in PROVIDER_CONFIGS;

export const getProviderSelectionFromProvider = (
  provider: string | null | undefined,
): SkillCollectionProviderSelection =>
  isSkillCollectionPreset(provider) ? provider : VENDOR_AGNOSTIC_PROVIDER;

export const providerSelectionToPreset = (
  selection: SkillCollectionProviderSelection | null | undefined,
): SkillCollectionPreset | null => (isSkillCollectionPreset(selection) ? selection : null);

export const providerSelectionToApiValue = (
  selection: SkillCollectionProviderSelection | null | undefined,
): SkillCollectionPreset | null | undefined => {
  if (selection === undefined) {
    return undefined;
  }

  return providerSelectionToPreset(selection);
};

export const getProviderSelectionLabelFromProvider = (
  provider: string | null | undefined,
): string => PROVIDER_SELECTION_LABELS[getProviderSelectionFromProvider(provider)];

export const getProviderSelectionLogoProvider = (
  selection: SkillCollectionProviderSelection,
): string | null =>
  selection === VENDOR_AGNOSTIC_PROVIDER ? null : PRESET_LOGO_PROVIDERS[selection];
